import {
  ProfileData,
  RepresentedTypes,
  SelectedCompaniesTypes,
} from 'store/modules/profile_store/types';
import tabId from './tabId';

export const ALL_COMPANIES = 'all';

export type CompanyToggle = number | typeof ALL_COMPANIES;

export const companyName = (company: {
  razao_social?: string | null;
  nome_fantasia?: string | null;
}): string => company.razao_social || company.nome_fantasia || '';

export const isAllSelected = (selected: number[], allIds: number[]) =>
  allIds.length > 0 && allIds.every((id) => selected.includes(id));

export const getSelectedCompanies = (profile: ProfileData, tab: string = tabId()): number[] => {
  const stored = profile.selectedCompanies?.[tab];
  const current = profile.currentCompany?.[tab];
  const fromCurrent = current && current !== profile.clientId ? [current] : [];
  const selected = profile.alfa_tester ? stored ?? fromCurrent : fromCurrent;
  const allowedIds = [
    ...(profile.represented || []).map((company) => company.id),
    profile.clientId,
  ];

  return selected.filter((id) => allowedIds.includes(id));
};

export const keepAllSelected = (
  selections: SelectedCompaniesTypes,
  before: RepresentedTypes[] | null | undefined,
  after: RepresentedTypes[] | null | undefined,
  ownId?: number,
): SelectedCompaniesTypes => {
  const beforeIds = (before || []).map(({ id }) => id);
  const afterIds = (after || []).map(({ id }) => id);
  if (beforeIds.length === 0) return selections;
  const added = afterIds.filter((id) => id !== ownId || beforeIds.includes(id));
  const allSelected = (selected: number[]) =>
    isAllSelected(selected, beforeIds) &&
    (beforeIds.length >= 2 || selected.some((id) => !beforeIds.includes(id)));

  return Object.fromEntries(
    Object.entries(selections || {}).map(([tab, selected]) => [
      tab,
      allSelected(selected)
        ? Array.from(new Set([...selected.filter((id) => !beforeIds.includes(id)), ...added]))
        : selected,
    ]),
  );
};

export const toggleCompany = (
  selected: number[],
  clicked: CompanyToggle,
  allIds: number[],
): number[] => {
  if (clicked === ALL_COMPANIES) return isAllSelected(selected, allIds) ? [] : [...allIds];
  if (selected.includes(clicked)) return selected.filter((id) => id !== clicked);

  return allIds.filter((id) => id === clicked || selected.includes(id));
};

export const ownCompany = (profile: ProfileData): RepresentedTypes | null => {
  const linked = (profile.represented || []).find(({ id }) => id === profile.clientId);
  if (linked) return linked;

  const data = profile.company_data?.data;
  if (!data) return null;

  return {
    id: profile.clientId,
    razao_social: data.razao_social,
    nome_fantasia: data.nome_fantasia,
    cnpj: data.cnpj,
    paralisado: false,
    sale_situation_name: '',
  };
};

export const selectableCompanies = (profile: ProfileData): RepresentedTypes[] => {
  const clients = (profile.represented || []).filter(({ id }) => id !== profile.clientId);
  const own = ownCompany(profile);

  return own ? [own, ...clients] : clients;
};

export const getSelectedRepresented = (
  profile: ProfileData,
  tab: string = tabId(),
): RepresentedTypes[] => {
  const selected = getSelectedCompanies(profile, tab);

  return selectableCompanies(profile).filter(({ id }) => selected.includes(id));
};

export type CompanyRequirement = 'single' | 'any';

export const FEATURE_REQUIREMENT: CompanyRequirement = 'single';

const REQUIRED_COMPANY: Array<{ path: string; requirement: CompanyRequirement }> = [
  { path: '/painel', requirement: 'any' },
  { path: '/boletim_web/public/boletins', requirement: 'single' },
  { path: '/banco_de_dados', requirement: 'single' },
  { path: '/iminencia', requirement: 'single' },
  { path: '/minhas_licitacoes/management', requirement: 'single' },
  { path: '/minhas_licitacoes/configuracoes', requirement: 'single' },
  { path: '/documentos', requirement: 'single' },
  { path: '/dr_licita', requirement: 'single' },
  { path: '/facilitadores/robo', requirement: 'single' },
  { path: '/robo_lance', requirement: 'single' },
  { path: '/meu_historico', requirement: 'single' },
  { path: '/validar_contrato', requirement: 'single' },
  { path: '/conlicitaseg', requirement: 'single' },
  { path: '/conlicita_go', requirement: 'single' },
  { path: '/assinatura_digital', requirement: 'single' },
  { path: '/conlicitacao_api', requirement: 'single' },
  { path: '/ajuda', requirement: 'single' },
  { path: '/portais', requirement: 'single' },
  { path: '/facilitadores/trading_keywords', requirement: 'single' },
  { path: '/facilitadores/monitoramento', requirement: 'any' },
];

export const companyRequirement = (pathname: string): CompanyRequirement | null => {
  const match = REQUIRED_COMPANY.find(
    ({ path }) => pathname === path || pathname.startsWith(`${path}/`),
  );

  return match ? match.requirement : null;
};

export const missingRequiredCompany = (
  requirement: CompanyRequirement | null,
  selected: number[],
) => {
  if (requirement === 'single') return selected.length !== 1;
  if (requirement === 'any') return selected.length === 0;

  return false;
};

export const selectionLabel = (selected: number[], represented: RepresentedTypes[]): string => {
  if (selected.length === 0) return 'Selecione uma empresa';

  if (selected.length === 1) {
    const company = represented.find(({ id }) => id === selected[0]);
    return company ? companyName(company) : 'Selecione uma empresa';
  }

  const allIds = represented.map(({ id }) => id);
  if (isAllSelected(selected, allIds)) return 'Todas as empresas';

  return `${selected.length} empresas selecionadas`;
};

export const withoutReleasedServices = (company: RepresentedTypes, profile: ProfileData) =>
  company.id !== profile.clientId && company.servicos_liberados === false;

export const firstReleasedCompany = (
  profile: ProfileData,
  companyIds: number[],
): number | undefined =>
  companyIds.find((id) => {
    const company = profile.represented?.find((item) => item.id === id);
    return !company || !withoutReleasedServices(company, profile);
  }) ?? companyIds[0];

export const blockedByReleasedServices = (
  profile: ProfileData,
  pathname: string,
  tab: string = tabId(),
): boolean => {
  if (!profile.representant || !companyRequirement(pathname)) return false;

  const selected = getSelectedRepresented(profile, tab);

  return (
    selected.length > 0 && selected.every((company) => withoutReleasedServices(company, profile))
  );
};
